"use client";

/**
 * "Görünüşcə oxşar": the active listings whose photos the photo-intel
 * model finds closest to this one's, kept to its market (GET
 * /api/listings/similar-by-look/{id}, services/image_search: the same
 * rayon or within 3 km, 0.5x to 2x of its price; feedback ticket 65). The
 * third of the listing's tabs (ListingAroundTabs on the listing page,
 * ListingAroundSection in the drawer): fetched once the tabs are about to
 * be seen, and only while photo search is switched on (admin features,
 * off by default while the photo index fills). A listing whose photos are
 * not read yet gets no tab at all (owner, 2026-10-19: no empty section and
 * no word about our index).
 */
import { useEffect, useState } from "react";
import { useFeatureFlag } from "@/lib/features";
import Link from "@/lib/localized-link";
import { fetchSimilarByLook, formatPrice } from "@/lib/api";
import { useT } from "@/lib/locale-context";
import { listingPath } from "@/lib/seo/paths";
import type { ListingCard } from "@/types";

export type Lookalike = ListingCard & { look_sim: number };

/** The lookalikes once ``start`` turns true: null until they are here
 *  (or for good while photo search is off), then the list (empty when
 *  none is near enough or the request failed). */
export function useLookalikes(listingId: string | null, limit: number, start: boolean): Lookalike[] | null {
  const on = useFeatureFlag("image_search", false);
  const [items, setItems] = useState<{ id: string; items: Lookalike[] } | null>(null);
  useEffect(() => {
    if (!on || !start || !listingId) return;
    let live = true;
    fetchSimilarByLook(listingId, { limit })
      .then((d) => { if (live) setItems({ id: listingId, items: d.items as Lookalike[] }); })
      .catch(() => { if (live) setItems({ id: listingId, items: [] }); });
    return () => { live = false; };
  }, [on, start, listingId, limit]);
  if (!on) return [];
  return items && items.id === listingId ? items.items : null;
}

/** The facts line of a small card: rooms, m², the place. */
function lookFacts(c: ListingCard, t: ReturnType<typeof useT>): string {
  return [
    c.room_count ? t("listings.magic.chip.rooms").replace("{n}", String(c.room_count)) : null,
    c.area_m2 ? `${Math.round(c.area_m2)} m²` : null,
    c.location_name ?? c.district ?? null,
  ].filter(Boolean).join(" · ");
}

/** The listing page's row of lookalikes: photo, price, facts. */
export function LookalikeGrid({ items }: { items: Lookalike[] }) {
  const t = useT();
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-6">
      {items.map((c) => {
        const lp = listingPath(c.id);
        return (
          <Link key={c.id} href={lp ?? "/listings"} className="group min-w-0">
            <span className="block aspect-[4/3] overflow-hidden rounded-xl bg-gray-100">
              {c.thumbnail_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={c.thumbnail_url} alt="" className="h-full w-full object-cover transition-transform group-hover:scale-[1.03]" loading="lazy" />
              ) : null}
            </span>
            <span className="mt-1.5 block truncate text-sm font-semibold text-gray-900 tabular-nums">
              {c.price != null ? `${formatPrice(c.price)} AZN` : t("common.na")}
            </span>
            <span className="block truncate text-[11px] text-gray-500">{lookFacts(c, t)}</span>
          </Link>
        );
      })}
    </div>
  );
}
